import React from 'react';
import { useMutation } from '@apollo/client';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { selectModalData } from '../../../../../redux/selectors/modal';
import {
  IUserTableFieldsFragment,
  IUserRevokeMutation,
  IUserRevokeMutationVariables,
} from '../../../../../utils/apollo/graphqlSchema';
import { closeModal } from '../../../../../redux/slices/modal';
import ConfirmationModal from '../../../../../components/ConfirmationModal';
import { USER_REVOKE } from '../../../../../utils/apollo/permission';
import printGraphqlErrors from '../../../../../utils/apollo/printGraphqlErrors';
import toastMessage from '../../../../../utils/toastMessage';

interface IProps {
  onSubmit: () => void;
}

const UserRevokeModal = ({ onSubmit }: IProps) => {
  const dispatch = useAppDispatch();

  const handleClose = () => {
    dispatch(closeModal());
  };

  const user = useAppSelector(selectModalData) as IUserTableFieldsFragment;

  const handleCompleteMutation = ({ userRevoke }: IUserRevokeMutation) => {
    if (printGraphqlErrors(userRevoke?.errors)) {
      return;
    }

    if (userRevoke?.revoked) {
      toastMessage({
        message: `User with email ${user.email} has been revoked.`,
        type: 'info',
      });

      handleClose();
      onSubmit();
    }
  };

  const [revokeUser, { loading }] = useMutation<
    IUserRevokeMutation,
    IUserRevokeMutationVariables
  >(USER_REVOKE, {
    onCompleted: handleCompleteMutation,
  });

  const handleSubmit = () => {
    revokeUser({
      variables: {
        input: {
          userId: user.id,
        },
      },
    });
  };

  return (
    <ConfirmationModal
      open
      onClose={handleClose}
      onSubmit={handleSubmit}
      header={'Revoke User'}
      confirmActionLabel={'Revoke'}
      modalType="danger"
      disabled={loading}
      trackContext="users"
      trackConfirmAction="revoke - confirm"
      trackCancelAction="revoke - cancel"
    >
      Are you sure you want to revoke {user.name}?
    </ConfirmationModal>
  );
};

export default UserRevokeModal;
